/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { processImage } from 'js/lib/imgix';

import { Button, breakpoints } from '@coursera/cds-core';
import { SvgPlayFilled } from '@coursera/coursera-ui/svg';

import type { DdpImageAssetFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Overlay from 'bundles/xddp/components/shared/Overlay';

import _t from 'i18n!nls/degree-description';

type Props = {
  quoteText: string;
  authorName: string;
  videoEmbedUrl?: string | null;
  placeholderImage: DdpImageAssetFragment;
  index: number;
  authorTitle?: string | null;
};

const styles = {
  videoPlaceholderContainer: (imageUrl: string) => css`
    cursor: pointer;
    ${breakpoints.up('sm')} {
      height: 434px;
    }
    ${breakpoints.down('sm')} {
      height: 200px;
    }

    position: relative;
    z-index: 3;
    width: 100%;
    border-radius: 8px;
    background-image: url(${imageUrl});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `,
  playIcon: css`
    position: absolute;
    z-index: 2;
    top: calc(50% - 24px);
    left: 50%;
    border: 3px solid var(--cds-color-neutral-primary-invert);
    border-radius: 36px;
    width: 66px;
    height: 66px;
    padding: 14px 14px;
    margin: 0 auto;

    :hover,
    :active,
    :focus {
      background-color: initial;
    }
  `,
  videoOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: var(--cds-color-neutral-primary);
    opacity: 0.2;
    border-radius: 6px;
  `,
  // Default dimensions to render Youtube video properly
  videoPlayerContainer: css`
    @media (min-width: 288px) {
      height: 128px;
    }

    @media (min-width: 576px) {
      height: 324px;
    }

    @media (min-width: 792px) {
      height: 445px;
    }

    @media (min-width: 1008px) {
      height: 567px;
    }

    @media (min-width: 1152px) {
      height: 648px;
    }
  `,
};

const TestimonialVideo: React.FC<Props> = ({
  quoteText,
  videoEmbedUrl,
  placeholderImage,
  index,
  authorName,
  authorTitle,
}) => {
  const [showVideoPlayback, setVideoPlayback] = useState(false);
  const { trackClickTestimonialCard } = useDegreeDescriptionEventingContext();

  if (!videoEmbedUrl || !placeholderImage) {
    // return skeleton?
    return null;
  }

  const compressedImageUrl = processImage(placeholderImage.url, {
    auto: 'format,compress',
    width: 550,
    quality: 60,
  });

  const handleOnClick = () => {
    if (showVideoPlayback === false) {
      trackClickTestimonialCard(quoteText, authorName, 'play_video', authorTitle, index);
    }

    setVideoPlayback(!showVideoPlayback);
  };

  const handleClose = () => {
    if (showVideoPlayback) {
      trackClickTestimonialCard(quoteText, authorName, 'exit_video_modal', authorTitle, index);
    }

    setVideoPlayback(!showVideoPlayback);
  };

  return (
    <TrackedDiv
      trackingName="testimonial"
      data={{ indexPosition: index + 1, testimonialName: authorName ?? '', testimonialTitle: authorTitle ?? '' }}
      withVisibilityTracking
      onClick={handleOnClick}
      role="button"
      aria-label={placeholderImage.description}
    >
      <div css={styles.videoPlaceholderContainer(compressedImageUrl)}>
        <Button variant="ghost" css={styles.playIcon} tabIndex={-1}>
          <SvgPlayFilled size={36} color="white" />
        </Button>
      </div>

      {showVideoPlayback && (
        <div data-testid="open-video-overlay">
          <Overlay handleClose={handleClose} isOpen={showVideoPlayback}>
            <div css={styles.videoPlayerContainer}>
              <iframe
                title={_t('Youtube Embedded Video')}
                width="100%"
                height="100%"
                src={videoEmbedUrl}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </Overlay>
        </div>
      )}
    </TrackedDiv>
  );
};

export default TestimonialVideo;
